import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { Database } from "../db/types.ts";
import { getBrowserClient } from "../supabase/browser.ts";
import { useAuthSession } from "./useAuthSession.ts";

export type UserProfile = Database["public"]["Tables"]["users"]["Row"];

export interface UserProfileState {
  profile: UserProfile | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const UserProfileContext = createContext<UserProfileState | null>(null);

interface UserProfileProviderProps {
  children: ReactNode;
}

// Single source of truth for the authenticated user's public.users row.
// RLS is configured (migration 0011 + 0013) so the anon-key client filters
// to auth.uid() automatically — this reads only the current user's row.
// Never use the service-role admin client here.
//
// Phase 1A Step 41 — race-guarded loading transition.
//
// Previously, `!user` branched immediately to `setLoading(false)`. When the
// auth session transitioned from null → truthy across two renders (Supabase
// `getSession()` resolves async), there was a one-render window where
// `session === truthy && profile === null && loading === false`. AuthGate
// read that as "user authenticated but missing profile" and redirected to
// /onboarding/age, which then bounced to /brief — making every non-/brief
// route appear hardcoded to /brief in production.
//
// The fix: only release `loading` in the `!user` branch when auth has also
// finished loading (authLoading === false). While auth is still resolving,
// keep loading=true so consumers see a stable "still loading" signal until
// the next refetch fires for the new user.
export function UserProfileProvider({ children }: UserProfileProviderProps): JSX.Element {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user, loading: authLoading } = useAuthSession();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!user) {
      setProfile(null);
      setError(null);
      // Only release the loading state when auth has truly resolved. While
      // authLoading is still true, keep loading=true to avoid the
      // session-truthy / profile-null / loading-false race described above.
      if (!authLoading) {
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from("users")
      .select("*")
      .eq("id", user.id)
      .single();
    if (fetchError) {
      setProfile(null);
      setError(fetchError.message);
    } else {
      setProfile(data);
    }
    setLoading(false);
  }, [supabase, user, authLoading]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const value = useMemo<UserProfileState>(
    () => ({ profile, loading, error, refetch }),
    [profile, loading, error, refetch],
  );

  return <UserProfileContext.Provider value={value}>{children}</UserProfileContext.Provider>;
}
